import React from "react";
import { Dialog } from "@mui/material";
import "./UploadResultDialog.scss";
import ButtonStyle from "../../../components/ButtonStyle/ButtonStyle";
import { SCHOOL_ADMIN, TEACHER_ROLE } from "../../../utils/constants";
import { useAppSelector } from "../../../redux/app/hooks";

interface Classroom {
  c_id: string;
  c_name: string;
}

export interface UploadResult {
  filename: string;
  duplicates: number;
  classroomsFailed?: Classroom[];
  classrooms_failed: Classroom[];
  classrooms_updated: Classroom[];
  skipped_no_subject: number;
  classrooms_considered: Classroom[];
  classrooms_failed_count: number;
  classrooms_updated_count: number;
  classrooms_total_considered: number;
}

export interface UploadResultForTeacher {
  results: {
      filename: string;
      status: string;
      message?: string;
  }[];
  failed_count: number;
  uploaded_count: number;
  files_processed: number;
}

export interface UploadResultForWorkshop {
  files_processed: number;
  uploaded_count: number;
  failed_count: number;
  results: {
      error_message: string;
      filename: string;
      status: string;
  }[];
}

type TeacherWorkshopResultItem =
  | { filename: string; status: string; message?: string }
  | { filename: string; status: string; error_message: string };
interface UploadDialogProps {
  open: boolean;
  onClose: () => void;
  results: UploadResult[];
  uploadResultsForTeacher?: UploadResultForTeacher | UploadResultForWorkshop;
  page: string;
}

const UploadResultsDialog: React.FC<UploadDialogProps> = ({
  open,
  onClose,
  results,
  uploadResultsForTeacher,
  page
}) => {

  const role = useAppSelector((state) => state.auth.role);
  
  const adminSuccessFiles = results.filter(
    (f) =>
      (f.classrooms_updated?.length ?? 0) > 0 ||
      f.classrooms_updated_count > 0
  );

  const adminFailedFiles = results.filter(
    (f) =>
      (f.classrooms_failed?.length ?? 0) > 0 ||
      (f.classroomsFailed?.length ?? 0) > 0 ||
      f.classrooms_failed_count > 0
  );


  console.log("[Upload Results] --- ", results);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      classes={{
        paper: "upload-bulk-popup",
      }}
    >
      <div className="upload-dialog">
        <div className="upload-dialog__header">
          <div className="heading">{`Upload Results`}</div>
          <span style={{fontSize: '20px'}}>
            Total files: {(role === SCHOOL_ADMIN) && (page !== 'workshopupload') ? results?.length : uploadResultsForTeacher?.files_processed}
          </span>
          <span className="upload-dialog__close" onClick={onClose}>
            ✕
          </span>
        </div>

        <div className="upload-dialog__content">
          {(role === SCHOOL_ADMIN) && (page !== 'workshopupload') && (
            <div className="upload-dialog__file-block">
              
              {/* SUCCESS SECTION */}
              {adminSuccessFiles.length > 0 && (
                <div className="upload-dialog__section success">
                  <h3 style={{color: 'green'}}>{`File(s) uploaded successfully:`}</h3>
                  {adminSuccessFiles.map((file, index) => (
                    <div key={index} style={{marginBottom: '15px'}}>
                      <strong>{index + 1}. File Name:</strong> {file.filename}
                      {(page === 'BulkUploadAdmin') && <div style={{paddingLeft: '15px'}}>
                        <strong>{`Class(es):`}</strong>
                        &nbsp;&nbsp;{file.classrooms_updated?.map(cls => cls.c_name).join(", ")}
                      </div>}
                    </div>
                  ))}
                </div>
              )}

              {/* FAILED SECTION */}
              {adminFailedFiles.length > 0 && (
                <div className="upload-dialog__section failed" style={{marginTop: '20px'}}>
                  <h3 style={{color: 'red'}}>{`Upload failed, please check`}</h3>
                  {adminFailedFiles.map((file, index) => {
                    const failedClassrooms =
                      file.classroomsFailed || file.classrooms_failed || [];
                    return (
                      <div key={index} style={{marginBottom: '15px'}}>
                        <strong>{index + 1}. File Name:</strong> {file.filename}
                        <div className="error" style={{padding: '5px 0px 8px 15px'}}>
                          <strong>Error:</strong>&nbsp;Duplicate filename detected. Please rename your file and try again.
                        </div>
                        {(page === "BulkUploadAdmin") && <div style={{paddingLeft: '15px'}}>
                          <strong>{`Class(es):`}</strong>
                          &nbsp;&nbsp;{failedClassrooms.map(cls => cls.c_name).join(", ")}
                        </div>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}


          {((role === TEACHER_ROLE) || ((role === SCHOOL_ADMIN) && (page === 'workshopupload')))&& uploadResultsForTeacher && (
          <div className="upload-dialog__file-block teacher">

            {/*Success Files Section */}
            {(uploadResultsForTeacher.results.some(f => f.status === "uploaded") || uploadResultsForTeacher.results.some(f => f.status === "success"))  && (
              <div className="upload-dialog__section success" style={{marginBottom: '20px'}}>
                <h3 className="upload-dialog__filename" style={{color: 'green'}}>{`File(s) uploaded successfully:`}</h3>
                {(uploadResultsForTeacher.results as TeacherWorkshopResultItem[])
                  .filter(f => (f.status === "uploaded" || f.status === "success"))
                  .map((file, index) => (
                    <div key={index} style={{ marginBottom: "12px" }}>
                      <div><strong>{index + 1}. File Name:</strong> {file.filename}</div>
                    </div>
                  ))}
              </div>
            )}

            {/* Failed Files Section */}
            {(uploadResultsForTeacher.results.some(f => f.status !== "uploaded") || uploadResultsForTeacher.results.some(f => f.status !== "success")) && (
              <div className="upload-dialog__section failed">
                <h3 className="upload-dialog__filename" style={{color: 'red'}}>{`Upload failed, please check`}</h3>
                {(uploadResultsForTeacher.results as TeacherWorkshopResultItem[])
                  .filter(f => f.status === "error")
                  .map((file, index) => {
                    const errorText = (file as any).message || (file as any).error_message;
                  return (
                    <div key={index} style={{ marginBottom: "12px" }}>
                      <div><strong>{index + 1}. File Name:</strong> {file.filename}</div>
                      <div style={{padding: '5px 0 0 13px'}}><span className="error"><strong>Error:</strong></span>&nbsp;
                      {errorText?.includes("Duplicate")
                        ? "Duplicate filename detected. Please rename your file and try again."
                        : errorText}</div>
                    </div>
                  )
                  })}
              </div>
            )}

          </div>
        )}

        </div>
        <div className="okay-btn">
        <ButtonStyle onClick={onClose} text="Okay" width="100px"/>
        </div>
      </div>
    </Dialog>
  );
};

export default UploadResultsDialog;
